import { Grid, Typography } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import TextFieldCustom from "../../../components/TextFieldCustom/TextFieldCustom";
import CalendarRange from "../../../components/CalendarRange/CalendarRange";
import CustomButton, {
  CustomButtonVariant,
} from "../../../components/CustomButton/CustomButton";
import MultipleSelectCheckbox from "../../../components/MultipleSelectCheckbox";
import { useDistributor } from "../../../contexts/distributor/DistributorContext";
import {
  convertToOptionsList,
  getFirstDayOfCurrentMonth,
} from "../../../utils";
import { GetAllNfeQuery } from "../../../modules/financial/models/GetAllNfeQuery";
import { ROW_PER_PAGE } from "../../ConsultQuotaOrders/models/SearchFilterOptions";
import { useOrderQuote } from "../../../contexts/orderQuote/OrderQuoteContext";
import { StyledIcon } from "../../../components/StyledIcon/StyledIcon";
import { convertToISO } from "../../../utils/dateFormatter";

interface SelectItem {
  value: string;
  label: string;
}

interface InvoiceFilterTableProps {
  onButtonChange: (query: any) => void;
  onButtonClearFilter: (isClear: boolean) => void;
}

const InvoicesFilter: React.FC<InvoiceFilterTableProps> = ({
  onButtonChange,
  onButtonClearFilter,
}) => {
  const { getProducts } = useOrderQuote();
  const { selectedDistributor, distributors } = useDistributor();

  const [codeProducts, setCodeProducts] = useState<string[]>([]);
  const [materialNumberStorage] = useState("");
  const [products, setProducts] = useState<SelectItem[]>([]);
  const [clearValueCalendar, setClearValueCalendar] = useState<boolean>(false);
  const [searchQueryNote, setSearchQueryNote] = useState<string>("");
  const [distributorsMapped, setDistributorsMapped] = useState<SelectItem[]>(
    []
  );
  const [codeDistributors, setCodeDistributors] = useState<string[]>([]);
  const storageExternalId = selectedDistributor?.externalId;
  const currentDay = new Date();
  const firstDay = new Date(currentDay.getFullYear(), currentDay.getMonth(), 1);

  const initFilters: GetAllNfeQuery = {
    orderBy: "creationDate",
    orderDirection: "desc",
    service: "NFE",
    customerNumber: storageExternalId,
    documentDateBegin: firstDay.toISOString(),
    documentDateEnd: currentDay.toISOString(),
    id: materialNumberStorage || "",
    page: 1,
    limit: ROW_PER_PAGE,
    electronicInvoiceNumber: searchQueryNote || "",
  };

  const [filters, setFilters] = useState<GetAllNfeQuery | null>(initFilters);
  const handleChangeSelectedCompanies = useCallback(
    (event: string[]) => {
      const allProductValues = products.map((product) => product.value);
      const isSelectAll =
        event.length === allProductValues.length &&
        allProductValues.every((val) => event.includes(val));

      const updatedSelection = isSelectAll ? [""] : event;

      setCodeProducts(updatedSelection);

      setFilters((oldState) => ({
        ...oldState,
        id: isSelectAll ? "" : updatedSelection.join(","),
      }));
    },
    [products, setCodeProducts, setFilters]
  );
  const handleChangeSelectedDistributors = useCallback(
    (event: string[]) => {
      if (event?.length > 0) {
        const selectedItem = event?.join(",");
        setCodeDistributors(event);
        setFilters((oldState) => ({
          ...oldState,
          customerNumber: selectedItem,
        }));
      } else {
        setCodeDistributors(event);
        onButtonClearFilter(true);

        if (filters) {
          setFilters((oldState) => ({
            ...oldState,
            customerNumber: "",
          }));
        }
      }
    },
    [setCodeDistributors, setFilters]
  );

  const handleSearchChangeNote = useCallback(
    (event?: React.ChangeEvent<HTMLInputElement> | any) => {
      if (event?.target) {
        const data = event.target.value.trim();

        if (data !== searchQueryNote) {
          setSearchQueryNote(data);
        }
        setFilters((oldState) => ({
          ...oldState,
          electronicInvoiceNumber: data.padStart(9, "0"),
        }));
      }
    },
    [setSearchQueryNote, searchQueryNote, setFilters]
  );

  const clearFilters = useCallback(() => {
    const customerNumber = distributorsMapped.map((ele) => ele.value).join(",");
    setClearValueCalendar(true);
    setSearchQueryNote("");
    setCodeProducts([""]);
    setCodeDistributors(distributorsMapped.map((ele) => ele.value));
    setFilters({
      ...initFilters,
      customerNumber: customerNumber,
      electronicInvoiceNumber: "",
      id: "",
    });
    onButtonChange({
      ...initFilters,
      customerNumber: customerNumber,
      electronicInvoiceNumber: "",
      id: "",
    });
    onButtonClearFilter(true);
  }, [
    distributorsMapped,
    firstDay,
    currentDay,
    onButtonChange,
    onButtonClearFilter,
  ]);

  const getProductsData = useCallback(async () => {
    const response = await getProducts?.();
    if (!response) return;

    const products = response.map((product: any) => ({
      value: product.externalId,
      label: product.description,
    }));

    const productsMapped: SelectItem[] = [...products];
    setProducts(productsMapped);
    if (codeProducts.length) {
      setCodeProducts([""]);
    }
  }, [getProducts]);

  const hasInitialized = useRef(false);

  const getDistributorsData = useCallback(async () => {
    if (!distributors?.length || hasInitialized.current) return;

    const distributorsMapped: SelectItem[] = distributors?.map(
      (product: any) => ({
        value: product.externalId,
        label: product.description,
      })
    );
    const itensCode = distributorsMapped.map((ele) => ele.value);

    hasInitialized.current = true;
    setDistributorsMapped(distributorsMapped);
    setCodeDistributors(itensCode);
    setCodeProducts([""]);
    setFilters((oldState) => ({
      ...oldState,
      customerNumber: itensCode?.join(","),
      ...(!oldState?.documentDateBegin && {}),
    }));
  }, [distributors]);

  const isReadyToLoad = distributors.length && products.length;
  const shouldDisableFields = !codeDistributors.length || !codeProducts.length;

  useEffect(() => {
    if (!products.length && !hasInitialized.current) {
      getProductsData();
    }
    if (isReadyToLoad && !hasInitialized.current) {
      getDistributorsData();
      hasInitialized.current = true;
      const allDistributors = distributors
        .map((distributor) => distributor.externalId)
        .join(",");

      const initialQuery = {
        ...initFilters,
        customerNumber: allDistributors || "",
        id: "",
      };
      setFilters(initialQuery);
      onButtonChange(initialQuery);
    }
  }, [distributors, products, getDistributorsData]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={9}>
        <Grid container spacing={2}>
          <Grid item xs={10} sm={6} md={3}>
            <MultipleSelectCheckbox
              maxWidth={1200}
              minWidth={100}
              label="Selecione o cliente"
              options={convertToOptionsList(
                distributorsMapped,
                "value",
                "label"
              )}
              value={codeDistributors}
              onChange={handleChangeSelectedDistributors}
            />
          </Grid>
          <Grid item xs={10} sm={6} md={3}>
            <Typography
              sx={{
                color: "#58595B",
                fontFamily: "Open Sans",
                fontSize: "14px",
                fontWeight: "700",
                paddingBottom: "4px",
              }}
            >
              Nota Fiscal
            </Typography>
            <TextFieldCustom
              type="number"
              inputProps={{
                min: 0,
              }}
              placeholder="Busque pelo Número da nota"
              value={searchQueryNote}
              onChange={handleSearchChangeNote}
              endComponent={<StyledIcon iconType="search" />}
              isDisabled={shouldDisableFields}
            />
          </Grid>
          <Grid item xs={10} sm={6} md={3}>
            <MultipleSelectCheckbox
              maxWidth={1200}
              minWidth={100}
              label="Produtos"
              isDisabled={!codeDistributors.length}
              options={convertToOptionsList(products, "value", "label")}
              value={codeProducts}
              onChange={handleChangeSelectedCompanies}
              sendEmptyWhenAllSelected={true}
            />
          </Grid>
          <Grid item xs={10} sm={6} md={3}>
            <CalendarRange
              name="date"
              label="Período de Emissão"
              firstDayOfMonth={getFirstDayOfCurrentMonth()}
              lastDayOfMonth={currentDay}
              value={[
                filters?.documentDateBegin
                  ? new Date(filters?.documentDateBegin)
                  : firstDay,
                filters?.documentDateEnd
                  ? new Date(filters?.documentDateEnd)
                  : currentDay,
              ]}
              clearValueCalendar={clearValueCalendar}
              disabledTextField={shouldDisableFields}
              onChangeDateStartEnd={(dateBegin, dateEnd) => {
                setClearValueCalendar(false);
                if (
                  dateBegin !== null &&
                  dateEnd !== null &&
                  dateBegin.getTime() === dateEnd.getTime()
                ) {
                  setFilters((oldState) => ({
                    ...oldState,
                    documentDateBegin: convertToISO(dateBegin),
                    documentDateEnd: convertToISO(dateEnd),
                  }));
                } else {
                  if (dateBegin !== null && dateEnd !== null) {
                    setFilters((oldState) => ({
                      ...oldState,
                      documentDateBegin: convertToISO(dateBegin),
                      documentDateEnd: convertToISO(dateEnd),
                    }));
                  }
                }
              }}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid
        item
        xs={3}
        container
        alignItems="flex-end"
        justifyContent={"flex-end"}
        alignContent={"flex-start"}
        marginTop={"25px"}
      >
        <CustomButton
          title="Limpar filtros"
          variant={CustomButtonVariant.OUTLINED}
          onClick={clearFilters}
        />

        <CustomButton
          sx={{ marginLeft: "16px" }}
          type="submit"
          title="Consultar"
          variant={
            shouldDisableFields
              ? CustomButtonVariant.DISABLED
              : CustomButtonVariant.CONTAINED
          }
          disabled={shouldDisableFields}
          onClick={() => {
            onButtonClearFilter(false);
            onButtonChange(filters);
          }}
        />
      </Grid>
    </Grid>
  );
};

export default InvoicesFilter;
// eslint-disable-next-line prettier/prettier
